import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Typography } from "@mui/material";
import { useSelector } from "react-redux";

import messages from "../../../../../assets/locale/messages";
import { UIActionModal, UIButton, UICallout, UIIcon } from "../../../../ui";
import { getTagEligibility } from "../tagEligibility";
import "./TagModals.scss";

const fill = (template, values) =>
  Object.entries(values).reduce(
    (text, [key, value]) => text.replaceAll(`{${key}}`, value),
    template ?? "",
  );

// alertCircle has a 38-unit viewBox shown at 20px, so a 2px visual stroke
// needs strokeWidth ≈ 2 * (38 / 20).
const calloutIcon = (
  <UIIcon
    name="alertCircle"
    width={20}
    height={20}
    color="var(--color-destructive-default, #DC2626)"
    strokeWidth={3.8}
    decorative
  />
);

const DeleteTagConfirmModal = ({
  open,
  selectedApplicants,
  portalVariant,
  myCompanyId,
  onClose,
  onConfirm,
}) => {
  const lang = useSelector((state) => state.locale.lang);
  const t =
    messages[lang].events.viewEvents.applicantsTab.joiningRequests
      .deleteTagModal;

  const { untaggedCount, otherOwnerCount, eligibleUserIds, total } = useMemo(
    () =>
      getTagEligibility(selectedApplicants, {
        portalVariant,
        myCompanyId,
        mode: "remove",
      }),
    [selectedApplicants, portalVariant, myCompanyId],
  );

  const noneToDelete = eligibleUserIds.length === 0 && otherOwnerCount === 0;
  const skipCount = untaggedCount + otherOwnerCount;
  const skipMessage =
    skipCount === 0
      ? null
      : untaggedCount > 0 && otherOwnerCount === 0
        ? fill(t.deleteUntaggedSkip, {
            count: untaggedCount,
            remaining: eligibleUserIds.length,
          })
        : fill(t.deleteOtherOwnerSkip, {
            count: skipCount,
            remaining: eligibleUserIds.length,
          });

  const renderCallout = (description) => (
    <UICallout
      variant="error"
      className="event-applicants-tag-callout event-applicants-tag-callout--destructive"
      icon={calloutIcon}
      title={description}
    />
  );

  if (noneToDelete) {
    return (
      <UIActionModal
        open={open}
        onClose={onClose}
        title={t.title}
        variant="reject"
        maxWidth="xs"
        PaperProps={{ className: "event-applicants-delete-tag-modal" }}
        actions={
          <div className="ui-modal__actions ui-action-modal__actions">
            <UIButton variant="outline" size="medium" fullWidth onClick={onClose}>
              {t.cancel}
            </UIButton>
          </div>
        }
      >
        {renderCallout(fill(t.noneToDelete, { count: total }))}
      </UIActionModal>
    );
  }

  const message = selectedApplicants.length > 1 ? t.deleteMultiple : t.deleteSingle;

  return (
    <UIActionModal
      open={open}
      onClose={onClose}
      title={t.title}
      variant="reject"
      maxWidth="xs"
      PaperProps={{ className: "event-applicants-delete-tag-modal" }}
      actions={
        <UIActionModal.Actions
          cancelLabel={t.cancel}
          confirmLabel={t.deleteConfirm}
          confirmVariant="reject"
          onCancel={onClose}
          onConfirm={onConfirm}
        />
      }
    >
      <Typography className="event-applicants-delete-tag-modal__message">
        {message}
      </Typography>
      {skipMessage && renderCallout(skipMessage)}
      <Typography className="event-applicants-delete-tag-modal__question">
        {t.deleteQuestion}
      </Typography>
    </UIActionModal>
  );
};

DeleteTagConfirmModal.propTypes = {
  open: PropTypes.bool.isRequired,
  selectedApplicants: PropTypes.arrayOf(PropTypes.object),
  portalVariant: PropTypes.string,
  myCompanyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

DeleteTagConfirmModal.defaultProps = {
  selectedApplicants: [],
  portalVariant: "company-portal",
  myCompanyId: undefined,
};

export default DeleteTagConfirmModal;
